import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useResourceManager } from 'components/organisms/resource-management/managers';
import {
  UserRemovedFromProjectParams,
  useUserRemovedFromProject,
} from 'components/organisms/resource-management/scheduler-config/listeners';
import {
  ProjectsWithMembers,
  SchedulerReference,
} from 'components/organisms/resource-management/types';
import {
  resourceIdForProject,
  resourceIdForUserAsLeaf,
} from 'components/organisms/resource-management/utils/id';
import { AllocatedMembers } from 'data/templates/projects';
import { updateAllocatedMembersAction } from 'data/templates/projects/actions';

interface Props {
  schedulerRef: SchedulerReference;
  allocatedMembers: AllocatedMembers;
  project: ProjectsWithMembers;
  removeUserFromProjectInUI: ReturnType<
    typeof useResourceManager
  >['removeUserFromProjectInUI'];
}
export const useRemoveUsersManager = ({
  schedulerRef,
  project,
  removeUserFromProjectInUI,
  allocatedMembers,
}: Props): void => {
  const dispatch = useDispatch();
  const removeUserFromState = useCallback(
    (userId: number) => {
      dispatch(
        updateAllocatedMembersAction({
          teams: allocatedMembers.teams,
          users: allocatedMembers.users.filter((x) => x.userId !== userId),
        }),
      );
    },
    [allocatedMembers.teams, allocatedMembers.users, dispatch],
  );
  const removeUser = useCallback(
    (params: UserRemovedFromProjectParams) => {
      // TODO: figure out a way to get allocatedMinutes of user before deleting
      const userResource = schedulerRef.current.instance.resourceStore.getById(
        resourceIdForUserAsLeaf({
          projectId: project.projectId,
          userId: params.userResource.user.userId,
          referenceTeamId: params.userResource.referenceTeam?.teamId,
          roleId: params.userResource.role?.roleId,
        }),
      );

      if (userResource == null) {
        return {
          differenceInTotalMinutes: 0,
        };
      }

      const result = {
        differenceInTotalMinutes: -1 * userResource.totalAllocation,
      };

      removeUserFromState(params.userResource.user.userId);
      removeUserFromProjectInUI(params);

      const projectLevelResource =
        schedulerRef.current.instance.resourceStore.getById(
          resourceIdForProject(project.projectId),
        );

      if (projectLevelResource != null) {
        projectLevelResource.set(
          'totalAllocation',
          projectLevelResource.get('totalAllocation') +
            (result?.differenceInTotalMinutes ?? 0),
          result,
        );
      }

      return result;
    },
    [
      project.projectId,
      removeUserFromProjectInUI,
      removeUserFromState,
      schedulerRef,
    ],
  );

  useUserRemovedFromProject(schedulerRef, removeUser);
};
